import React, { CSSProperties, useCallback, useContext } from 'react';
import { AngularServicesContext } from 'react-app';
import { css } from '@emotion/react';
import t from 'react-translate';

import { Submission } from 'redux/schemas/models/submissions';

import BaseCard, { CARD_WIDTH } from 'offerings/components/base-card';
import ClickableContainer from 'components/clickable-container';
import SubmissionHeader from 'shared/components/submission/submission-header';
import SubmissionExtraContent from 'shared/components/submission/submission-extra-content';

import { semiBoldFontWeight, textSmallFontSize } from 'styles/global_defaults/fonts';
import {
  doubleSpacing, halfSpacing, largeSpacing, quarterSpacing,
  standardSpacing, threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import { black, gray2, gray5, primary, success, warning } from 'styles/global_defaults/colors';

type NvSubmissionCardProps = React.ComponentPropsWithoutRef<'div'> & {
  submission: Submission;
  style: CSSProperties;
  showStatus?: boolean,
  statusClass?: string,
  statusContent?: JSX.Element,
  stateParams?: {
    galleryMode?: boolean,
    isRatingFeedback?: boolean,
  };
  showApprovalStatus?: boolean
  showRevisionRemainderTime?: boolean
  hideSocial?: boolean
  showApprovalPendingTime?: boolean
  showDueDate?: boolean
  onClickSubmission?: () => void
};

const NvSubmissionCard = React.forwardRef<HTMLDivElement, NvSubmissionCardProps>((props, ref) => {
  const { $state } = useContext(AngularServicesContext);
  const {
    submission,
    showStatus,
    statusClass,
    statusContent,
    style,
    showApprovalStatus = true,
    hideSocial = false,
    showRevisionRemainderTime,
    showApprovalPendingTime,
    onClickSubmission,
    showDueDate,
  } = props;
  const {
    id,
    exercise,
    title,
    coverPhotoUrl,
  } = submission;
  const backgroundImage = coverPhotoUrl ? `url(${coverPhotoUrl})` : '';

  const styles = css`
    display: block;
    max-width: ${CARD_WIDTH}px;

  &:hover {
    color: unset;
  }

  .submission-card {
    width: 100%;
    background-color: white;

    &:hover {
      border: 1px solid ${primary};
    }

    .card-header {
      height: 120px;

      .backdrop-layer {
        position: relative;
      }

      .status-wrapper {
        position: absolute;
        top: 0;
        width: 100%;
        height: ${showStatus ? doubleSpacing : largeSpacing}px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        font-weight: ${semiBoldFontWeight};
        font-size: ${textSmallFontSize}px;
        ${showStatus && css`opacity: .8;`}

        &.bg-color-primary {
          background-color: ${primary};
        }

        &.bg-color-success {
          background-color: ${success};
        }

        &.bg-color-warning {
          background-color: ${warning};
        }

        &.bg-color-gray {
          background-color: ${gray2};
        }
      }

      .header-main-content {
        width: 100%;
        height: 100%;

        .course-logo {
          width: 100%;
          height: 100%;
          background-color: ${exercise?.course?.headerColor};
          background-image: ${backgroundImage};
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          border-bottom: 1px solid ${gray5};
        }

        .submission-header {
          width: 100%;
          height: 100%;
          padding-top: ${exercise?.approvalRequired ? doubleSpacing : threeQuartersSpacing}px;
          padding-bottom: ${exercise?.approvalRequired ? quarterSpacing : halfSpacing}px;
          padding-left: ${halfSpacing}px;
          padding-right: ${halfSpacing}px;
          border-bottom: 1px solid ${gray5};
          overflow: hidden;

          .body {
            width: 100%;
            height: 100%;
            overflow: hidden;
          }
        }
      }


      .owner-type-info {
        .nv-user-avatar {
          position: absolute;
          bottom: -44px;
          left: 50%;
          transform: translate(-50%);
          margin: 0;

          .avatar-container {
            border-radius: 50%;
            border: 3px solid white;
          }
        }

        .nv-team-avatar {
          position: absolute;
          top: 98px;
          left: 50%;
          transform: translate(-50%);
          margin: 0;
          max-width: 100%;
          > div:first-of-type {
            border: 3px solid white;
          }

          .team-name {
            color: ${black};
            font-weight: normal;
            padding: 0 ${standardSpacing}px;
            margin-top: ${quarterSpacing}px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .card-content {
      flex: 1;

      .submission-name {
        margin-top: 54px;
        padding: 0 ${standardSpacing}px;
      }
    }

    .extra {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: end;

      .social {
        display: flex;
        gap: ${standardSpacing}px;
        justify-content: center;
        padding-bottom: ${threeQuartersSpacing}px;
        margin-top: ${quarterSpacing}px;

        .likes {
          display: flex;
          gap: ${quarterSpacing}px;
        }

        .comments {
          display: flex;
          gap: ${quarterSpacing}px;
        }
      }
    }
  }
`;

  const goToSubmissionModal = () => $state.go('individual-submission-modal', {
    catalogId: exercise?.catalogId,
    reportId: id,
    ...(props.stateParams ?? {}),
  });

  const renderHeader = useCallback(() => (
    <SubmissionHeader
      submission={submission}
      showStatus={showStatus}
      statusClass={statusClass}
      statusContent={statusContent}
      showApprovalStatus={showApprovalStatus}
      showApprovalPendingTime={showApprovalPendingTime}
    />
  // listing only the affecting dependencies from submission object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [
    showApprovalPendingTime, showApprovalStatus, showStatus, statusClass,
    statusContent, exercise, coverPhotoUrl, submission.body, submission.owner,
    submission.ownerType, submission.submitting,
  ]);

  const renderContent = useCallback(() => (
    <React.Fragment>
      <h2
        className='card-title ellipsis submission-name text-center'
        id={`submission_title_${submission.id}`}
        aria-label={exercise?.title
          ? t.EXERCISES.SUBMISSION_TITLE_DESCRIPTION(exercise?.title, title)
          : t.EXERCISES.VIEW_SUBMISSION_DETAILS(title)}
      >
        {title}
      </h2>
      {exercise?.title && (
        <h3 className='text-small gray-2 ellipsis mx-4 mt-1 text-center'>
          {exercise.title}
        </h3>
      )}
    </React.Fragment>
  ), [exercise?.title, title, submission.id]);

  const renderExtra = useCallback(() => (
    <SubmissionExtraContent
      submission={submission}
      hideSocial={hideSocial}
      showRevisionRemainderTime={showRevisionRemainderTime}
      showDueDate={showDueDate}
    />
  // listing only the affecting dependencies from submission object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [
    hideSocial, showDueDate, showRevisionRemainderTime, exercise,
    submission.isVotable,
    submission.votesCount,
    submission.numPostsAndComments,
    submission.submitting,
  ]);

  return (
    <ClickableContainer
      ref={ref}
      css={styles}
      style={style}
      onClick={onClickSubmission ?? goToSubmissionModal}
      aria-labelledby={`submission_title_${submission.id}`}
      aria-describedby={`submission_description_${submission.id}`}
    >
      <BaseCard
        layoutOnly
        className='submission-card'
        renderHeader={renderHeader}
        renderContent={renderContent}
        renderExtraContent={renderExtra}
        style={style}
      />
    </ClickableContainer>
  );
});

export default NvSubmissionCard;
